import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import usePrefersReducedMotion from "../hooks/usePrefersReducedMotion";
import type { TimelineMilestone } from "../data/timeline";
import ScrollReveal from "./ScrollReveal";

interface TimelineTrackProps {
  milestones: TimelineMilestone[];
}

function MilestoneBody({ milestone }: { milestone: TimelineMilestone }) {
  return (
    <>
      {milestone.image && (
        <img
          src={milestone.image.src}
          alt={milestone.image.alt}
          title={milestone.image.credit}
          loading="lazy"
          className="mt-2 h-32 w-full rounded-lg object-cover"
        />
      )}
      <p className="mt-1.5 text-sm leading-relaxed text-ocean-800/70">{milestone.body}</p>
      {milestone.link && (
        <Link
          to={milestone.link.href}
          onClick={(e) => e.stopPropagation()}
          className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-teal-600 hover:text-teal-700"
        >
          {milestone.link.label}
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      )}
    </>
  );
}

export default function TimelineTrack({ milestones }: TimelineTrackProps) {
  const [activeYear, setActiveYear] = useState<string | null>(null);
  const reducedMotion = usePrefersReducedMotion();
  const toggle = (year: string) => setActiveYear((cur) => (cur === year ? null : year));

  return (
    <>
      {/* Desktop: horizontal scroll-snap track, tap a milestone to expand */}
      <div className="hidden lg:block">
        <div className="flex snap-x snap-mandatory gap-6 overflow-x-auto pb-6 [scrollbar-width:thin]">
          {milestones.map((m, i) => {
            const expanded = activeYear === m.year;
            return (
              <ScrollReveal key={m.year} delay={i * 0.08} className="w-72 shrink-0 snap-start">
                <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-ocean-900/5 transition-colors hover:ring-teal-400/40">
                  <button
                    type="button"
                    onClick={() => toggle(m.year)}
                    aria-expanded={expanded}
                    className="w-full text-left"
                  >
                    <span className="flex h-4 w-4 items-center justify-center rounded-full bg-teal-400 ring-4 ring-foam-50" />
                    <span className="mt-3 block text-xs font-semibold uppercase tracking-widest text-teal-700">
                      {m.year}
                    </span>
                    <h3 className="mt-1 font-display text-lg font-semibold text-ocean-950">{m.title}</h3>
                  </button>
                  {expanded && (
                    <motion.div
                      initial={reducedMotion ? false : { opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      transition={{ duration: reducedMotion ? 0 : 0.25 }}
                      className="overflow-hidden"
                    >
                      <MilestoneBody milestone={m} />
                    </motion.div>
                  )}
                </div>
              </ScrollReveal>
            );
          })}
        </div>
      </div>

      {/* Mobile/tablet: vertical stacked accordion */}
      <div className="space-y-8 border-l-2 border-teal-400/30 pl-8 lg:hidden">
        {milestones.map((m, i) => {
          const expanded = activeYear === m.year;
          return (
            <ScrollReveal key={m.year} delay={i * 0.08} className="relative">
              <span className="absolute -left-[38px] flex h-4 w-4 items-center justify-center rounded-full bg-teal-400 ring-4 ring-foam-50" />
              <button type="button" onClick={() => toggle(m.year)} aria-expanded={expanded} className="w-full text-left">
                <span className="text-xs font-semibold uppercase tracking-widest text-teal-700">{m.year}</span>
                <h3 className="mt-1 font-display text-lg font-semibold text-ocean-950">{m.title}</h3>
              </button>
              {expanded && (
                <motion.div
                  initial={reducedMotion ? false : { opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  transition={{ duration: reducedMotion ? 0 : 0.25 }}
                  className="overflow-hidden"
                >
                  <MilestoneBody milestone={m} />
                </motion.div>
              )}
            </ScrollReveal>
          );
        })}
      </div>
    </>
  );
}
